import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, Injector } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { convertToParamMap, NavigationEnd, Router } from '@angular/router';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { StitchServiceFactory } from '@modules/common/factories/stitch-service.factory';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { ModalService } from '@modules/modal/services/modal.service';
import { filter, firstValueFrom, merge, Subject, take, takeUntil } from 'rxjs';
import { readTeamItemLink, type TeamItemLink } from './team-item-link';

@Component({ selector: 'app-team-item-link-opener', template: '', changeDetection: ChangeDetectionStrategy.OnPush })
export class TeamItemLinkOpenerComponent {
  private readonly router = inject(Router);
  private readonly context = inject(SpaceContextStore);
  private readonly accountKind = inject(AccountKindService);
  private readonly factory = inject(StitchServiceFactory);
  private readonly modal = inject(ModalService);
  private readonly toaster = inject(ToasterService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly workspace = toObservable(computed(() => this.context.contextId()));
  private readonly newerAttempt = new Subject<void>();
  private handledLink: string | null = null;
  private attempt = 0;

  public constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.readAddress(event.urlAfterRedirects));
    this.readAddress(this.router.url);
  }

  private readAddress(address: string): void {
    const tree = this.router.parseUrl(address);
    const params = convertToParamMap(tree.queryParams);
    if (!['team', 'type', 'item'].every((key) => params.has(key))) {
      this.handledLink = null;
      return;
    }
    const key = JSON.stringify([params.get('team'), params.get('type'), params.get('item')]);
    if (key === this.handledLink) return;
    this.handledLink = key;
    const attempt = ++this.attempt;
    this.newerAttempt.next();
    const query = { ...tree.queryParams };
    delete query.team;
    delete query.type;
    delete query.item;
    tree.queryParams = query;
    void this.router.navigateByUrl(tree, { replaceUrl: true }).catch(() => undefined);
    const link = readTeamItemLink(params);
    if (!link) {
      this.toaster.show({ text: 'This item link is not valid.' });
      return;
    }
    if (!this.accountKind.isTeam() || !this.context.insideTeam() || this.context.contextId() !== link.team) {
      this.toaster.show({
        text: 'This link opens an item in another workspace. Switch to that team, then open the link again.',
      });
      return;
    }
    void this.openItem(link, attempt);
  }

  private async openItem(link: TeamItemLink, attempt: number): Promise<void> {
    try {
      const full = await firstValueFrom(
        this.factory
          .getStitchItemById(link.type, link.itemId, { tenantItems: [{ accountId: link.team }] })
          .pipe(
            take(1),
            takeUntil(merge(this.newerAttempt, this.workspace.pipe(filter((team) => team !== link.team)))),
            takeUntilDestroyed(this.destroyRef),
          ),
        { defaultValue: null },
      );
      if (!full || !this.current(attempt, link.team)) return;
      this.modal.openFullForm(full, this.injector, owningTenantContext(link.team));
    } catch {
      if (this.current(attempt, link.team)) this.toaster.show({ text: 'The item could not be opened.' });
    }
  }

  private current(attempt: number, team: string): boolean {
    return !this.destroyRef.destroyed && attempt === this.attempt && this.context.contextId() === team;
  }
}
